import Emitter     from 'es6-event-emitter';
import isAscii     from 'validator/isAscii';
import isBoolean   from 'validator/isBoolean';
import isEmail     from 'validator/isEmail';
import isEmpty     from 'validator/isEmpty';
import isFloat     from 'validator/isFloat';
import isInt       from 'validator/isInt';
import isIp        from 'validator/isIP';
import isMultibyte from 'validator/isMultibyte';
import isNumeric   from 'validator/isNumeric';
import isURL       from 'validator/isURL';
import toBoolean   from 'validator/toBoolean';
import toDate      from 'validator/toDate';
import toFloat     from 'validator/toFloat';
import toInt       from 'validator/toInt';
import trim        from 'validator/trim';

/**
 * 検証クラス
 *
 * @package     croco-fw
 * @author      Yujiro Takahashi <yujiro@cro-co.co.jp>
 */
class Validator extends Emitter {
    /**
     * コンストラクタ
     *
     * @access public
     * @param  array opts
     */
    constructor(opts) {
        super();

        this._analyses = new Map();

        this._keys = Object.keys(opts);
        for (let idx in this._keys) {
            this[this._keys[idx]] = opts[this._keys[idx]];
            this._analyses.set(this._keys[idx], true);
        } // for (let key of itor)
    }

    /**
     * 必須チェック
     *
     * @access public
     * @param  string  name
     * @param  boolean required
     * @return boolean
     */
    isRequired(name, required) {
        if (isEmpty(String(this[name]))) {
            if (required) {
                this._analyses.set(name, false);
                return false;
            }
        }
        return true;
    }

    /**
     * データセット
     *
     * @access public
     * @param  string name
     * @param  mixed  value
     * @param  array  validation
     * @return boolean
     */
    set(name, value, validation) {
        let result = false;
        this[name] = value;

        if (isEmpty(String(value))) {
            if (!validation.required) {
                result = true;
            }
        } else {
            if (this.validation(value, validation.validate)) {
                this[name] = this.normalizer(value, validation.validate);
                result = true;
            }
        }
        this._analyses.set(name, result);

        if (result) {
            this.trigger('set:' + name, this[name]);
        }
        return result;
    }

    /**
     * データセット
     *
     * @access public
     * @param  string name
     * @param  mixed  value
     * @param  array  validation
     * @return boolean
     */
    setArray(name, values, validation) {
        let result = false;
        let list = new Array();

        for (let idx in values) {
            let value = values[idx];
            if (this.validation(value, validation.validate)) {
                list.push(
                    this.normalizer(value, validation.validate)
                );
            }
        } // for (let idx in values) {
        this[name] = list;

        if (validation.required) {
            result = (list.length != 0);
        } else {
            result = true;
        } // if (!validation.required)

        this._analyses.set(name, result);
        if (result) {
            this.trigger('set:' + name, this[name]);
        }
        return result;
    }


    /**
     * 正規化処理
     *
     * @access public
     * @param  mixed  value
     * @param  string type
     * @return mixed
     */
    normalizer(value, type) {
        let normalizers = new Map([
            ['ascii',     this._through],
            ['bool',      this.toBoolean],
            ['boolean',   this.toBoolean],
            ['date',      this._toDate],
            ['email',     this._through],
            ['mail',      this._through],
            ['float',     this._toFloat],
            ['doble',     this._toFloat],
            ['int',       this._toInt],
            ['integer',   this._toInt],
            ['ip',        this._through],
            ['multibyte', this._through],
            ['mbstring',  this._through],
            ['string',    this._through],
            ['numeric',   this._toNumeric],
            ['number',    this._toNumeric],
            ['url',       this._through]
        ]);
        let normalize = normalizers.has(type)
                ? normalizers.get(type) : normalizers.get('string');
        return normalize(value);
    }

     /**
     * 値の検証
     *
     * @access public
     * @param  mixed  value
     * @param  string type
     * @return boolean
     */
    validation(value, type) {
        let filters = new Map([
            ['ascii',     isAscii],
            ['bool',      isBoolean],
            ['boolean',   isBoolean],
            ['date',      this._pass],
            ['email',     isEmail],
            ['mail',      isEmail],
            ['float',     isFloat],
            ['doble',     isFloat],
            ['int',       isInt],
            ['integer',   isInt],
            ['ip',        isIp],
            ['multibyte', isMultibyte],
            ['mbstring',  isMultibyte],
            ['string',    this._pass],
            ['numeric',   isNumeric],
            ['number',    isNumeric],
            ['url',       isURL]
        ]);

        let filter = filters.has(type) ? filters.get(type) : filters.get('string');
        let val = new String(value);
        try {
            return filter(val);
        } catch(e) { }

        return false;
    }

    /**
     * 検証結果の集計
     *
     * @access public
     * @return array
     */
    toError(arg='error') {
        let val = new String(arg);
        let ok = '', ng = val;

        if (isBoolean(val)) {
            ok = true;
            ng = false;
        } else if (isInt(val)) {
            ok = 1;
            ng = 0;
        }

        let result = {};
        for (let [name, value] of this._analyses) {
            if (value) {
                result[name] = ok;
            } else {
                result[name] = ng;
            }
        }
        return result;
    }

    /**
     * データへ変換・取得
     *
     * @access public
     * @return void
     */
    toData() {
        let data = {};
        for (let idx in this._keys) {
            data[this._keys[idx]] = this[this._keys[idx]];
        } // for (let idx in this._keys)

        return data;
    }


    /**
     * データの更新
     *
     * @access public
     * @return void
     */
    update(data) {
        this._analyses.clear();
        let keys = Object.keys(data);
        for (let idx in keys) {
            this[keys[idx]] = data[keys[idx]];
            this._analyses.set(keys[idx], true);
        } // for (let key of itor)
    }

    /**
     * 検証結果の集計
     *
     * @access public
     * @return boolean
     */
    validate() {
        let result = true;
        for (let [name, value] of this._analyses) {
            if (!value) {
                result = false;
            }
            this.trigger('valid:'+ name, value);
        }
        return result;
    }

    /**
     * 何も検証しない
     *
     * @access private
     * @param  mixed  value
     * @return boolean
     */
    _pass(value) {
        return true;
    }

    /**
     * 何もしない
     *
     * @access private
     * @param  mixed  value
     * @return mixed
     */
    _through(value) {
        return value;
    }

    /**
     * 真偽値への変換
     *
     * @access private
     * @param  mixed  value
     * @return boolean
     */
    _toBoolean(value) {
        return toBoolean(trim(value));
    }

    /**
     * 日付オブジェクトへの変換
     *
     * @access private
     * @param  mixed  value
     * @return Date
     */
    _toDate(value) {
        return toDate(trim(value));
    }

    /**
     * Float型への変換
     *
     * @access private
     * @param  mixed  value
     * @return Float
     */
    _toFloat(value) {
        return toFloat(trim(value));
    }

    /**
     * Int型への変換
     *
     * @access private
     * @param  mixed  value
     * @return Int
     */
    _toInt(value) {
        return toInt(trim(value));
    }

    /**
     * Numberオブジェクトへの変換
     *
     * @access private
     * @param  mixed  value
     * @return Number
     */
    _toNumeric(value) {
        return new Number(trim(value));
    }
} // class Validator

export default Validator;
